import React, {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Button, Result, Spin} from "antd";
import {useParams, useSearchParams} from "react-router-dom";
import "../Styles/PublicRegistrationPage.css";
import {formatMoney} from "../format";
import {LanguageSwitch, PublicShell} from "../Components/ui";
import type {PublicLanguage} from "../Components/ui";
import {publicPaymentLabelKeys} from "../i18n/publicPages";
import {usePublicPageLabels} from "../i18n/publicPageLabels";

type PaymentInfo = {
    offerNumber: string; offerType: string; remainingAmount: number; refundedAmount: number;
    currency: string; status: string; canPay: boolean;
};
const PublicStripePaymentPage = () => {
    const {token = ""} = useParams();
    const [query, setQuery] = useSearchParams();
    const language = query.get("lang") === "en" ? "en" : "hr";
    const labels = usePublicPageLabels("public-payment", publicPaymentLabelKeys, language);
    const returned = query.get("result") === "returned";
    const [info, setInfo] = useState<PaymentInfo>();
    const [error, setError] = useState(false);
    const [checkoutError, setCheckoutError] = useState(false);
    const [statusCheckError, setStatusCheckError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const startingRef = useRef(false);
    const requestSequence = useRef(0);
    const providerChecks = useRef(0);
    const endpoint = `/api/public/stripe-payments/${encodeURIComponent(token)}`;
    const refresh = useCallback(async (checkProvider = false) => {
        const sequence = ++requestSequence.current;
        if (checkProvider) ++providerChecks.current;
        try {
            let response: Response | undefined;
            let providerFailed = false;
            if (checkProvider) {
                try {
                    response = await fetch(`${endpoint}/refresh`, {method: "POST", credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer"});
                    if (!response.ok) {providerFailed = true; response = undefined;}
                } catch {providerFailed = true;}
            }
            response ??= await fetch(endpoint, {credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer"});
            if (!response.ok) throw new Error();
            const data = await response.json();
            if (sequence === requestSequence.current) {
                setInfo(data); setError(false);
                if (checkProvider || data.status === "paid") setStatusCheckError(providerFailed && data.status !== "paid");
            }
        } catch {
            if (sequence === requestSequence.current) setError(true);
        } finally {
            if (checkProvider) --providerChecks.current;
            if (sequence === requestSequence.current) setLoading(false);
        }
    }, [endpoint]);
    useEffect(() => {
        setInfo(undefined); setLoading(true); setCheckoutError(false); setStatusCheckError(false);
        void refresh(returned);
        return () => {++requestSequence.current;};
    }, [refresh, returned]);
    useEffect(() => {
        if (info?.status !== "pending" || !returned) return;
        let attempts = 0;
        const timer = window.setInterval(() => {
            if (++attempts > 20) window.clearInterval(timer);
            else if (providerChecks.current === 0) void refresh();
        }, 3000);
        return () => window.clearInterval(timer);
    }, [info?.status, returned, refresh]);
    const checkout = async () => {
        if (startingRef.current) return;
        startingRef.current = true;
        setStarting(true); setCheckoutError(false);
        try {
            const response = await fetch(`${endpoint}/checkout`, {method: "POST", credentials: "omit", referrerPolicy: "no-referrer"});
            if (!response.ok) throw new Error();
            const data = await response.json();
            const url = new URL(data.checkoutUrl);
            if (url.protocol !== "https:" || url.hostname !== "checkout.stripe.com" || url.username || url.password) throw new Error();
            window.location.assign(url.href);
        } catch {setCheckoutError(true);}
        finally {startingRef.current = false; setStarting(false);}
    };
    const paid = info?.status === "paid";
    const pending = info?.status === "pending";
    const review = info?.status === "review";
    const changeLanguage = (lang: PublicLanguage) => {
        const next = new URLSearchParams(query); next.set("lang", lang); setQuery(next, {replace: true});
    };
    return <PublicShell live toolbar={<LanguageSwitch value={language} onChange={changeLanguage} label={labels.language}/>}>
            {loading ? <Spin/> : error || !info ? <Result status="warning" title={labels.unavailable} subTitle={labels.help}
                extra={<Button onClick={() => void refresh(true)}>{labels.refresh}</Button>}/> : <Result
                status={paid ? "success" : review || !info.canPay && !pending ? "warning" : "info"}
                title={paid ? labels.paid : review ? labels.review : pending ? labels.pending : info.canPay ? labels.title : labels.unavailable}
                subTitle={review ? labels.reviewHelp : pending ? labels.pendingHelp : !info.canPay && !paid ? labels.help : labels.secure}
                extra={<div>
                    <p>{info.offerNumber}, {info.offerType === "Accommodation" ? labels.accommodation : labels.participation}</p>
                    <p>{labels.remaining}: {formatMoney(info.remainingAmount, info.currency, language)}</p>
                    {info.refundedAmount > 0 && <p>{labels.refunded}: {formatMoney(info.refundedAmount, info.currency, language)}</p>}
                    {query.get("result") === "cancelled" && !paid && <Alert type="info" message={labels.cancelled}/>}
                    {checkoutError && <Alert type="error" message={labels.failed}/>}
                    {statusCheckError && <Alert type="warning" message={labels.statusCheckFailed}/>}
                    {info.canPay && <Button type="primary" disabled={starting} loading={starting} onClick={() => void checkout()}>{labels.pay}</Button>}
                    <Button disabled={starting} onClick={() => void refresh(true)}>{labels.refresh}</Button>
                </div>}/>}
    </PublicShell>;
};
export default PublicStripePaymentPage;
